(() => {
  if (customElements.get('camplux-navigation')) return;
  class CampluxNavigation extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.triggers = [...this.querySelectorAll('[data-cp-trigger]')];
      this.header = this.closest('store-header') || this.closest('.header');
      this.backdrop = this.querySelector('[data-cp-backdrop]');
      this.desktop = window.matchMedia('(min-width: 1200px)');
      this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.active = null;
      this.keyboardMode = false;
      this.animations = new Map();
      this.warmedMenus = new WeakSet();
      const listen = (target, event, handler, options = {}) =>
        target?.addEventListener(event, handler, { ...options, signal: this.controller.signal });
      listen(this, 'pointerdown', () => { this.keyboardMode = false; });
      listen(this, 'pointermove', event => {
        this.previousPoint = this.pointerPoint;
        this.pointerPoint = { x: event.clientX, y: event.clientY, time: performance.now() };
        if (event.target.closest('[data-cp-category][aria-selected="true"]')) this.selectedPoint = this.pointerPoint;
      }, { passive: true });
      listen(this, 'click', event => {
        const trigger = event.target.closest('[data-cp-trigger]');
        if (trigger) {
          if (!this.desktop.matches) return;
          event.preventDefault();
          this.open(trigger);
          return;
        }
        const tab = event.target.closest('[data-cp-category]');
        if (tab) this.selectCategory(tab);
        if (event.target.closest('[data-cp-close], [data-cp-backdrop]')) this.close(true);
      });
      listen(this, 'keydown', event => {
        this.keyboardMode = true;
        this.cancelCategory();
        this.onKeydown(event);
      });
      this.triggers.forEach(trigger => {
        listen(trigger, 'pointerenter', event => {
          if (!this.canHover(event) || this.dataset.hover !== 'true') return;
          this.clearTimers();
          this.warmMenu(trigger);
          this.openTimer = window.setTimeout(() => this.open(trigger), this.active ? 90 : 130);
        });
        listen(trigger, 'pointerleave', () => window.clearTimeout(this.openTimer));
      });
      this.querySelectorAll('[data-cp-category]').forEach(tab => {
        listen(tab, 'pointerenter', event => {
          if (!this.canHover(event) || tab.getAttribute('aria-selected') === 'true') return;
          this.cancelCategory();
          window.clearTimeout(this.closeTimer);
          this.pendingTab = tab;
          this.warmPanel(this.menuFor(this.active)?.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));
          const delay = this.movingToPanel(event, tab) ? 240 : 90;
          this.categoryTimer = window.setTimeout(() => {
            if (this.pendingTab !== tab || !tab.matches(':hover')) return;
            this.pendingTab = null;
            this.selectCategory(tab);
          }, delay);
        });
        listen(tab, 'pointerleave', () => {
          if (this.pendingTab === tab) this.cancelCategory();
        });
      });
      this.querySelectorAll('[data-cp-menu]').forEach(menu => {
        listen(menu, 'pointerenter', () => {
          window.clearTimeout(this.closeTimer);
          window.clearTimeout(this.openTimer);
        });
      });
      this.querySelectorAll('.cp-mega__panels').forEach(panels => {
        listen(panels, 'pointerenter', () => this.cancelCategory());
      });
      listen(this.backdrop, 'pointerenter', () => this.queueClose());
      this.querySelectorAll('.cp-navigation__link:not([data-cp-trigger])').forEach(link => {
        listen(link, 'pointerenter', () => this.queueClose());
      });
      listen(this, 'pointerenter', () => window.clearTimeout(this.closeTimer));
      listen(this, 'pointerleave', () => {
        window.clearTimeout(this.openTimer);
        this.cancelCategory();
        this.queueClose();
      });
      listen(document, 'pointerdown', event => {
        if (this.active && !this.contains(event.target)) this.close();
      });
      listen(document, 'focusin', event => {
        if (this.active && !this.contains(event.target)) this.close();
      });
      listen(this.header, 'click', event => {
        if (!this.contains(event.target)) this.close();
      });
      listen(window, 'resize', () => {
        if (!this.desktop.matches) this.close();
        else this.updatePosition();
      });
      listen(window, 'scroll', () => {
        if (!this.active) return;
        const bounds = this.header?.getBoundingClientRect();
        if (bounds && bounds.bottom <= 0) this.close();
        else this.updatePosition();
      }, { passive: true });
      listen(document, 'shopify:block:select', event => {
        const block = [...this.querySelectorAll('[data-cp-block]')].find(item => item.dataset.cpBlock === event.detail?.blockId);
        const panel = block?.closest('[role="tabpanel"]');
        const menu = block?.closest('[data-cp-menu]');
        if (!menu || !panel) return;
        this.open(this.triggers.find(item => item.getAttribute('aria-controls') === menu.id));
        const tab = [...menu.querySelectorAll('[data-cp-category]')].find(item => item.getAttribute('aria-controls') === panel.id);
        if (tab) this.selectCategory(tab);
      });
      listen(document, 'shopify:section:deselect', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close();
      });
      if (window.ResizeObserver && this.header) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => this.updatePosition());
        this.resizeObserver.observe(this.header);
      }
      this.updatePosition();
    }
    disconnectedCallback() {
      this.close();
      this.clearTimers();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.animations?.forEach(timer => window.clearTimeout(timer));
    }
    canHover(event) {
      return this.desktop.matches && this.hover.matches && event.pointerType !== 'touch';
    }
    movingToPanel(event, tab) {
      const previous = this.selectedPoint;
      const panels = tab.closest('[data-cp-menu]')?.querySelector('.cp-mega__panels');
      if (!previous || !panels || performance.now() - previous.time > 300) return false;
      const bounds = panels.getBoundingClientRect();
      const rtl = getComputedStyle(this).direction === 'rtl';
      const direction = rtl ? -1 : 1;
      const distance = ((rtl ? bounds.right : bounds.left) - previous.x) * direction;
      const progress = (event.clientX - previous.x) * direction;
      if (distance <= 0 || progress <= 3 || progress > distance) return false;
      // Only protect a diagonal journey from the selected row into its content.
      // Entering a different row from above or the right uses the normal delay.
      const fraction = progress / distance;
      const top = previous.y + (bounds.top - 12 - previous.y) * fraction;
      const bottom = previous.y + (bounds.bottom + 12 - previous.y) * fraction;
      return event.clientY >= top && event.clientY <= bottom;
    }
    warmPanel(panel, priority = 'low') {
      panel?.querySelectorAll('img').forEach(image => {
        image.loading = 'eager';
        image.fetchPriority = priority;
      });
    }
    warmMenu(trigger) {
      const menu = this.menuFor(trigger);
      if (!menu || this.warmedMenus.has(menu)) return;
      this.warmedMenus.add(menu);
      this.warmPanel(menu);
      this.warmPanel(menu.querySelector('[role="tabpanel"]:not([hidden])'), 'auto');
    }

    cancelCategory() {
      window.clearTimeout(this.categoryTimer);
      this.pendingTab = null;
    }
    clearTimers() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.cancelCategory();
    }
    queueClose() {
      this.cancelCategory();
      if (!this.hover.matches || !this.active) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        if (!this.active) return;
        const menu = this.menuFor(this.active);
        if (this.keyboardMode && menu?.contains(document.activeElement)) return;
        this.close();
      }, 350);
    }
    menuFor(trigger) {
      const id = trigger?.getAttribute('aria-controls');
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }
    updatePosition() {
      const bounds = this.header?.getBoundingClientRect();
      if (!bounds) return;
      const top = `${Math.max(0, Math.round(bounds.bottom))}px`;
      if (this.style.getPropertyValue('--cp-panel-top') !== top) this.style.setProperty('--cp-panel-top', top);
    }
    animate(element, className) {
      window.clearTimeout(this.animations.get(element));
      element.classList.remove(className);
      element.classList.add(className);
      this.animations.set(element, window.setTimeout(() => {
        element.classList.remove(className);
        this.animations.delete(element);
      }, 230));
    }
    open(trigger) {
      if (!trigger || !this.desktop.matches) return;
      this.clearTimers();
      if (this.active === trigger) return;
      const previous = this.active;
      if (previous) {
        previous.setAttribute('aria-expanded', 'false');
        const oldMenu = this.menuFor(previous);
        if (oldMenu?.contains(document.activeElement)) trigger.focus({ preventScroll: true });
        if (oldMenu) oldMenu.hidden = true;
      }
      const menu = this.menuFor(trigger);
      if (!menu) return;
      this.active = trigger;
      this.selectedPoint = null;
      this.warmMenu(trigger);
      trigger.setAttribute('aria-expanded', 'true');
      menu.hidden = false;
      if (this.backdrop) this.backdrop.hidden = false;
      this.classList.add('is-open');
      this.updatePosition();
      if (!previous) this.animate(menu.querySelector('.cp-mega__surface') || menu, 'cp-menu-enter');
      if (!previous) this.dispatchEvent(new CustomEvent('desktop-nav:dropdown:open', { bubbles: true }));
    }
    close(restoreFocus = false) {
      this.clearTimers();
      if (!this.active) return;
      const previous = this.active;
      previous.setAttribute('aria-expanded', 'false');
      const menu = this.menuFor(previous);
      const focusWasInside = menu?.contains(document.activeElement);
      if (menu) menu.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.active = null;
      this.selectedPoint = null;
      this.classList.remove('is-open');
      this.dispatchEvent(new CustomEvent('desktop-nav:dropdown:close', { bubbles: true }));
      if (restoreFocus || focusWasInside) previous.focus({ preventScroll: true });
    }
    selectCategory(tab, focus = false) {
      const menu = tab.closest('[data-cp-menu]');
      if (!menu) return;
      this.cancelCategory();
      window.clearTimeout(this.closeTimer);
      if (tab.getAttribute('aria-selected') === 'true') {
        if (focus) tab.focus({ preventScroll: true });
        return;
      }
      let restoreTabFocus = focus;
      menu.querySelectorAll('[data-cp-category]').forEach(item => {
        const selected = item === tab;
        const panel = menu.querySelector(`#${CSS.escape(item.getAttribute('aria-controls'))}`);
        if (!selected && panel?.contains(document.activeElement)) restoreTabFocus = true;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        item.classList.toggle('is-selected', selected);
        if (panel) {
          panel.hidden = !selected;
          if (selected) this.animate(panel, 'cp-panel-enter');
        }
      });
      if (restoreTabFocus) tab.focus({ preventScroll: true });
    }
    onKeydown(event) {
      const trigger = event.target.closest('[data-cp-trigger]');
      if (trigger && ['ArrowDown', 'ArrowUp', ' '].includes(event.key)) {
        event.preventDefault();
        this.open(trigger);
        const menu = this.menuFor(trigger);
        const tabs = [...(menu?.querySelectorAll('[data-cp-category]') || [])];
        const target = event.key === 'ArrowUp' ? tabs.at(-1) : menu?.querySelector('[data-cp-category][aria-selected="true"]');
        if (target) this.selectCategory(target, true);
        return;
      }
      if (trigger && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        let index = this.triggers.indexOf(trigger);
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') index = (index + (rtl ? -1 : 1) + this.triggers.length) % this.triggers.length;
        if (event.key === 'ArrowLeft') index = (index + (rtl ? 1 : -1) + this.triggers.length) % this.triggers.length;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = this.triggers.length - 1;
        if (this.active) this.open(this.triggers[index]);
        this.triggers[index]?.focus({ preventScroll: true });
        return;
      }
      if (event.key === 'Escape' && this.active) {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key === 'ArrowLeft' && event.target.closest('[role="tabpanel"]')) {
        event.preventDefault();
        event.target.closest('[data-cp-menu]')?.querySelector('[data-cp-category][aria-selected="true"]')?.focus({ preventScroll: true });
        return;
      }
      const tab = event.target.closest('[data-cp-category]');
      if (tab && ['ArrowDown', 'ArrowUp', 'Home', 'End', 'ArrowRight'].includes(event.key)) {
        const panel = menu => menu.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        const menu = tab.closest('[data-cp-menu]');
        event.preventDefault();
        if (event.key === 'ArrowRight') {
          panel(menu)?.querySelector('a[href],button:not([disabled]),[tabindex="0"]')?.focus();
          return;
        }
        const tabs = [...menu.querySelectorAll('[data-cp-category]')];
        let index = tabs.indexOf(tab);
        if (event.key === 'ArrowDown') index = (index + 1) % tabs.length;
        if (event.key === 'ArrowUp') index = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = tabs.length - 1;
        this.selectCategory(tabs[index], true);
      }
    }
  }
  customElements.define('camplux-navigation', CampluxNavigation);
})();
